/**
 * Générateur — Exprimer une probabilité sous diverses formes.
 * Attendu ID : 170 | Niveau : 4ème | Domaine : Probabilités
 *
 * PLAFOND (attendus de fin de 4e, Eduscol, `.claude/reference/eduscol-attendus-fin-4e.md`) :
 *   l. 217 « Il exprime des probabilités sous diverses formes. »
 *   l. 240-244 (exemple de réussite) « Une urne contient 1 boule rouge et 4
 *          boules oranges. Combien y a-t-il de chances de tirer une boule
 *          orange ? À quelle probabilité cela correspond-il ? Les 4 chances
 *          sur 5 de tirer une boule orange correspondent à une probabilité
 *          égale à 4/5 ou 0,8. Il peut également verbaliser qu'il y a 80 % de
 *          chances de tirer une boule orange. »
 * Cours du prof (19Proba, p. 2-3) : « 8/10 = 0,8 … 8 chances sur 10 ou 80 % de
 * chance » ; 20/50 = 2/5 = 0,4 (40 %), 24/50 = 12/25 = 0,48 (48 %).
 *
 * Revue 2026-09-25 (vague « sept objectifs 4e fermés », DECISIONS_167-170.md) :
 * UNE carte « L'urne rouge et orange » (id `ecritures`), support tiré : urne à
 * boules rouges et orange (plafond), roue à secteurs égaux orange et bleus,
 * sac de jetons verts et orange ; jamais le dé (1/6 n'est pas décimal). Total
 * dans {4, 5, 10, 20, 25} (20 ou 25 dans un tirage sur deux : 7/20 → 0,35
 * teste le passage aux centièmes), effectif favorable de 1 à total − 1.
 *   - ecritures_texte_trou_saisie (Saisie trous, « La chaîne des écritures »,
 *     l. 240-244) : la situation, puis la chaîne du cours et du plafond,
 *     étiquetée pour verrouiller l'ordre : « Il y a … chances sur … de tirer
 *     une boule orange. Cette probabilité s'écrit … en fraction, … en écriture
 *     décimale et … en pourcentage. » avec DEUX écritures tirées parmi les
 *     quatre (chances sur, fraction, écriture décimale, pourcentage) laissées
 *     en trous, les deux autres écrites en clair : chaque sens de conversion
 *     sort au fil des tirages (6 paires, _meta.params.trous) ; aucun trou
 *     dicté par la consigne. La fraction se tape numérateur et dénominateur
 *     (deux trous) : le correcteur des trous n'admet qu'une égalité de
 *     chaîne, la consigne dit donc « La fraction a pour dénominateur le nombre
 *     total de boules » quand la fraction est un trou (DECISIONS : « sinon
 *     l'énoncé dit fraction de dénominateur total »).
 *     ÉCART SIGNALÉ : la phrase est rendue en lignes KaTeX globales
 *     (`$\text{…}$`, précédent gen_603 / gen_1110, FORMATS §0.5bis) et non en
 *     texte nu comme le demandait « pas de $ dans la phrase » : le lint
 *     MATH-TEXTE (gate 0/0) interdit une fraction en clair (« 12/25 » ou
 *     « {{12}}/{{25}} ») dans une phrase, et une fraction lue par l'élève
 *     doit être en KaTeX ; les mots sont dans la formule, la phrase est
 *     verbatim celle des DÉCISIONS, découpée en six lignes courtes (une ligne
 *     KaTeX ne se replie pas : la première débordait d'une page de 480 px).
 *     Les décimaux s'écrivent « 0{,}48 » en KaTeX (sans les accolades, une
 *     espace suit la virgule).
 *   - ecritures_qcm_unique (QCM, l. 242-244) : « Quelle est la probabilité de
 *     tirer une boule orange, en écriture décimale ? / en pourcentage ? / en
 *     fraction ? » (forme tirée) ; la clé dans la forme demandée ; le
 *     complément (principale) dans la même forme, puis deux distracteurs en
 *     rotation parmi des écritures mêlées ; quatre options distinctes par
 *     texte et par valeur, au plus une au-dessus de 1.
 * Rappel brouillon OUI sur les deux formats (12/25 = 48/100 se pose), pas de
 * calculatrice. « orange » invariable (« des boules orange »), « 1 chance sur »
 * au singulier. Figures en imageUrl avec alt (urne redessinée : vase ajusté aux
 * rangées, couleurs mélangées ; roue à secteurs égaux ; sac de jetons).
 *
 * SORTI (et pourquoi) : les cinq formats « La cible » (l. 238 est 168 ; le
 * contraire est 167 ; « Quel calcul ? » est une règle nue) ; le relier (nature
 * des écritures, pur rappel, quatre sources sans leurre) ; la carte « chances
 * sur » seule (recopie CM2, dictée par la consigne) ; le maillon pourcentage
 * seul ; les tirets cadratins de la phrase ; l'exigence « irréductible » ; le
 * QCM de juillet sur le comptage (redite de 168, trois options, « non
 * réduite » égale à la clé) ; le QCM multiple et le Vrai/Faux (non retenus).
 * SACRIFIÉS (jamais tirés) : (total 5 ; 4 orange) plafond l. 240, (total 10 ;
 * 8 orange) cours p. 2 ; 20/50 et 24/50 du cours sont hors plage.
 * Second chemin (throw) : les effectifs RELUS dans l'énoncé rendu, a × 100
 * divisible par le total, les quatre écritures recalculées en entiers et
 * comparées aux trous ; pour le QCM la clé recalculée dans la forme demandée.
 * Solveur test-side : tests/solvers/probabilites-4e.js ; auto-test :
 * tests/gen170.self.test.js.
 */
import { randomChoice, randInt, shuffle, makeQcm, makeTexteTrou, fracTex } from '../../../utils';
import { RAPPEL_BROUILLON, frCent, denombre, elUrneBoules, elRoueSecteurs, elSacJetons } from './probas';

const ATTENDU_ID = 170;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ECRITURES = "L'urne rouge et orange";

const strip = (html) => String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
const EPS = 1e-9;

// ─── Le support et la situation ──────────────────────────────

const TOTAUX_PETITS = [4, 5, 10];
const TOTAUX_GRANDS = [20, 25];
/** (total ; favorables) jamais tirés : plafond (5 ; 4), cours (10 ; 8). */
const SACRIFIES = new Set(['5/4', '10/8']);

/**
 * Le support (urne, roue, sac), le total (20 ou 25 un tirage sur deux), a
 * boules/secteurs/jetons orange (1 à total − 1), hors sacrifiés.
 */
export const tirerSituation = () => {
    for (let essai = 0; essai < 60; essai++) {
        const T = randomChoice(randInt(0, 1) === 0 ? TOTAUX_GRANDS : TOTAUX_PETITS);
        const a = randInt(1, T - 1);
        if (SACRIFIES.has(`${T}/${a}`)) continue;
        const support = randomChoice(['urne', 'roue', 'sac']);
        return { support, T, a, pct: (a * 100) / T };
    }
    return { support: 'urne', T: 20, a: 7, pct: 35 };   // repli déterministe vérifié
};

const SUPPORTS = {
    urne: {
        objet: 'boules', evenement: 'de tirer une boule orange',
        situation: (s) => `<p>Une urne contient ${denombre(s.T - s.a, 'boule', 'rouge')} et ${denombre(s.a, 'boule', 'orange', true)}. On tire une boule au hasard.</p>`,
        figure: (s) => elUrneBoules(shuffle([...Array(s.a).fill({ couleur: 'orange' }), ...Array(s.T - s.a).fill({ couleur: 'rouge' })]),
            `Urne de ${s.T} boules mélangées : ${denombre(s.T - s.a, 'boule', 'rouge')} et ${denombre(s.a, 'boule', 'orange', true)}.`),
    },
    // « d'obtenir un secteur orange » (la formulation de 1082) et non « que la
    // roue s'arrête sur un secteur orange » : cette ligne KaTeX débordait d'un
    // écran de 380 px (vu en PNG), celle-ci a la longueur de la ligne de l'urne.
    roue: {
        objet: 'secteurs', evenement: "d'obtenir un secteur orange",
        situation: (s) => `<p>Une roue de loterie a ${s.T} secteurs identiques : ${denombre(s.a, 'secteur', 'orange', true)} et ${denombre(s.T - s.a, 'secteur', 'bleu')}. On fait tourner la roue.</p>`,
        figure: (s) => elRoueSecteurs(shuffle([...Array(s.a).fill('orange'), ...Array(s.T - s.a).fill('bleue')]),
            `Roue de ${s.T} secteurs identiques : ${denombre(s.a, 'secteur', 'orange', true)} et ${denombre(s.T - s.a, 'secteur', 'bleu')}.`),
    },
    sac: {
        objet: 'jetons', evenement: 'de tirer un jeton orange',
        situation: (s) => `<p>Un sac contient ${denombre(s.T - s.a, 'jeton', 'vert')} et ${denombre(s.a, 'jeton', 'orange', true)}. On tire un jeton au hasard.</p>`,
        figure: (s) => elSacJetons(shuffle([...Array(s.a).fill({ couleur: 'orange' }), ...Array(s.T - s.a).fill({ couleur: 'verte' })]),
            `Sac de ${s.T} jetons mélangés : ${denombre(s.T - s.a, 'jeton', 'vert')} et ${denombre(s.a, 'jeton', 'orange', true)}.`),
    },
};

/** Second chemin : les effectifs RELUS dans l'énoncé rendu → { a, T }. */
const relireSituation = (texte) => {
    const t = strip(texte);
    let m = t.match(/Une urne contient (\d+) boules? rouges? et (\d+) boules? orange\./);
    if (m) return { a: Number(m[2]), T: Number(m[1]) + Number(m[2]) };
    m = t.match(/a (\d+) secteurs identiques : (\d+) secteurs? orange et (\d+) secteurs? bleus?\./);
    if (m) {
        if (Number(m[2]) + Number(m[3]) !== Number(m[1])) throw new Error('gen_170 : secteurs incohérents');
        return { a: Number(m[2]), T: Number(m[1]) };
    }
    m = t.match(/Un sac contient (\d+) jetons? verts? et (\d+) jetons? orange\./);
    if (m) return { a: Number(m[2]), T: Number(m[1]) + Number(m[2]) };
    throw new Error(`gen_170 : situation illisible « ${t} »`);
};

/** Les quatre écritures, en entiers : { a, T, pct (entier), dec (chaîne « 0,48 ») } ; throw si le pourcentage n'est pas entier. */
const ecrituresDe = (a, T) => {
    if ((a * 100) % T !== 0 || a <= 0 || a >= T) throw new Error(`gen_170 : ${a}/${T} n'a pas de pourcentage entier`);
    const pct = (a * 100) / T;
    return { a, T, pct, dec: frCent(pct) };
};

// ═════════════════════════════════════════════════════════════
// « La chaîne des écritures » : ecritures_texte_trou_saisie
// ═════════════════════════════════════════════════════════════

const ECRITURES = ['chances', 'fraction', 'decimale', 'pourcentage'];

/** « 0,48 » → « 0{,}48 » (KaTeX : sans les accolades, une espace suit la virgule). */
const decTex = (s) => s.replace(',', '{,}');

/**
 * Les SIX lignes KaTeX de la chaîne (mode KaTeX global : chaque ligne est
 * entièrement entre $…$, les mots dans \text{}), les écritures de `trous`
 * laissées en {{…}} (la fraction : deux trous, numérateur et dénominateur).
 * Six lignes courtes et non quatre : une ligne KaTeX en mode affichage ne se
 * replie jamais, et « Il y a 16 chances sur 20 que la roue s'arrête sur un
 * secteur orange. » débordait d'une page de 480 px (vu en PNG) ; chaque ligne
 * tient maintenant sous 250 px, un téléphone la montre entière.
 */
const lignesChaine = (e, trous, evenement) => {
    const t = (cle) => trous.includes(cle);
    const A = t('chances') ? `{{${e.a}}}` : String(e.a);
    const B = t('chances') ? `{{${e.T}}}` : String(e.T);
    const num = t('fraction') ? `{{${e.a}}}` : String(e.a);
    const den = t('fraction') ? `{{${e.T}}}` : String(e.T);
    const D = t('decimale') ? `{{${e.dec}}}` : decTex(e.dec);
    const P = t('pourcentage') ? `{{${e.pct}}}` : String(e.pct);
    return [
        `$\\text{Il y a } ${A} \\text{ chance${e.a > 1 ? 's' : ''} sur } ${B}$`,
        `$\\text{${evenement}.}$`,
        '$\\text{Cette probabilité s\'écrit}$',
        `$\\dfrac{${num}}{${den}} \\text{ en fraction,}$`,
        `$${D} \\text{ en écriture décimale}$`,
        `$\\text{et } ${P}\\,\\% \\text{ en pourcentage.}$`,
    ];
};

/** Les mots attendus des trous, dans l'ordre des lignes (second chemin). */
const motsAttendus = (e, trous) => {
    const out = [];
    if (trous.includes('chances')) out.push(String(e.a), String(e.T));
    if (trous.includes('fraction')) out.push(String(e.a), String(e.T));
    if (trous.includes('decimale')) out.push(e.dec);
    if (trous.includes('pourcentage')) out.push(String(e.pct));
    return out;
};

const genChaineTrous = () => {
    const s = tirerSituation();
    const S = SUPPORTS[s.support];
    const trous = shuffle(ECRITURES).slice(0, 2).sort((x, y) => ECRITURES.indexOf(x) - ECRITURES.indexOf(y));
    const situation = S.situation(s);
    const lu = relireSituation(situation);
    const e = ecrituresDe(lu.a, lu.T);
    if (e.a !== s.a || e.T !== s.T || e.pct !== s.pct) throw new Error('gen_170 : effectifs relus');
    const phrase = lignesChaine(e, trous, S.evenement).join('\n');
    // Second chemin : les trous de la phrase, relus, sont exactement les écritures recalculées.
    const relus = [...phrase.matchAll(/\{\{([^{}]+)\}\}/g)].map((m) => m[1]);
    if (relus.join('|') !== motsAttendus(e, trous).join('|')) throw new Error(`gen_170 : trous ${relus.join('|')}`);
    if (/—|\d{7,}/.test(phrase)) throw new Error('gen_170 : phrase');
    const consigne = trous.includes('fraction')
        ? `Complète la chaîne des écritures. La fraction a pour dénominateur le nombre total de ${S.objet}.`
        : 'Complète la chaîne des écritures.';
    const texte = `${situation}${RAPPEL_BROUILLON}<p><b>${consigne}</b></p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_ECRITURES), mode: 'saisie', strict: false });
    payload.elements_additionnels = S.figure(s);
    payload._meta.params = { support: s.support, T: s.T, a: s.a, pct: e.pct, dec: e.dec, trous };
    return payload;
};

// ═════════════════════════════════════════════════════════════
// « L'urne rouge et orange » : ecritures_qcm_unique
// ═════════════════════════════════════════════════════════════

const FORMES = { decimale: 'en écriture décimale', pourcentage: 'en pourcentage', fraction: 'en fraction' };

const ecrit = (forme, num, T, pct) => {
    if (forme === 'decimale') return { texte: frCent(pct), valeur: pct / 100 };
    if (forme === 'pourcentage') return { texte: `${pct} %`, valeur: pct / 100 };
    return { texte: fracTex(num, T), valeur: num / T };
};

/** Les candidats d'une situation (a orange sur T), chacun avec son code, son texte et sa valeur. */
const candidats = (e, forme) => {
    const digits = e.dec.split(',')[1] || '';
    const d = Number(digits);
    const out = [
        { code: 'complement', ...ecrit(forme, e.T - e.a, e.T, 100 - e.pct) },
        { code: 'fraction_a_virgule', texte: `${e.a},${e.T}`, valeur: Number(`${e.a}.${e.T}`) },
        { code: 'numerateur_en_pourcent', texte: `${e.a} %`, valeur: e.a / 100 },
        { code: 'pourcent_sans_cent', texte: `${e.dec} %`, valeur: e.pct / 10000 },
    ];
    if (e.a >= 2) out.push({ code: 'inverse', ...ecrit('fraction', e.T, e.a, 0) });
    if (e.pct % 10 === 0) out.push({ code: 'decalage_de_virgule', texte: frCent(e.pct / 10), valeur: e.pct / 1000 });
    else out.push({ code: 'decalage_de_virgule', texte: frCent(e.pct * 10), valeur: e.pct / 10 });
    if (d >= 2) out.push({ code: 'un_sur_n', ...ecrit('fraction', 1, d, 0) });
    return out;
};

/**
 * Trois distracteurs : le complément (premier de la liste) s'il est admissible,
 * complété par le premier couple admissible de la rotation mélangée, sinon le
 * premier triplet admissible (recherche exhaustive). Admissible : différents
 * de la clé et entre eux par le code, le texte ET la valeur, au plus un
 * au-dessus de 1. Throw s'il n'existe pas de triplet.
 */
const admissible = (cle, liste) => liste.every((c) => c && c.valeur > 0 && c.texte !== cle.texte && Math.abs(c.valeur - cle.valeur) > EPS)
    && liste.every((c, i) => liste.slice(i + 1).every((d) => d.code !== c.code && d.texte !== c.texte && Math.abs(d.valeur - c.valeur) > EPS))
    && liste.filter((c) => c.valeur > 1).length <= 1;

const choisirDistracteurs = (cle, liste) => {
    const [principal, ...reste] = liste;
    const r = shuffle(reste);
    if (admissible(cle, [principal])) {
        for (let i = 0; i < r.length; i++) {
            for (let j = i + 1; j < r.length; j++) {
                if (admissible(cle, [principal, r[i], r[j]])) return [principal, r[i], r[j]];
            }
        }
    }
    for (let i = 0; i < r.length; i++) {
        for (let j = i + 1; j < r.length; j++) {
            for (let k = j + 1; k < r.length; k++) {
                if (admissible(cle, [r[i], r[j], r[k]])) return [r[i], r[j], r[k]];
            }
        }
    }
    throw new Error(`gen_170 : distracteurs insuffisants pour ${cle.texte}`);
};

const genEcrituresQcm = () => {
    const s = tirerSituation();
    const S = SUPPORTS[s.support];
    const forme = randomChoice(['decimale', 'pourcentage', 'fraction']);
    const situation = S.situation(s);
    const lu = relireSituation(situation);
    const e = ecrituresDe(lu.a, lu.T);
    const cle = { code: null, ...ecrit(forme, e.a, e.T, e.pct) };
    if (Math.abs(cle.valeur - s.a / s.T) > EPS) throw new Error('gen_170 : clé');
    const distracteurs = choisirDistracteurs(cle, candidats(e, forme));
    const options = [cle, ...distracteurs];
    if (new Set(options.map((o) => o.texte)).size !== 4) throw new Error('gen_170 : options en double');
    const texte = `${situation}${RAPPEL_BROUILLON}<p><b>Quelle est la probabilité ${S.evenement}, ${FORMES[forme]} ?</b></p>`;
    const payload = makeQcm(options.map((o) => o.texte), cle.texte, texte, opts(L_ECRITURES));
    payload.elements_additionnels = S.figure(s);
    payload._meta.params = { support: s.support, T: s.T, a: s.a, pct: e.pct, dec: e.dec, forme, reponse: cle.texte, distracteurs: distracteurs.map((d) => d.code) };
    return payload;
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    ecritures_qcm_unique: {
        support: 'a orange sur un total T ∈ {4, 5, 10, 20, 25} (urne, roue à secteurs égaux, sac) ; « Quelle est la probabilité …, en écriture décimale / en pourcentage / en fraction ? » ; la clé dans la forme demandée, les distracteurs sous des écritures mêlées',
        principal: { complement: 'la probabilité de l\'autre couleur, dans la forme demandée (fiche 5e 1082 « 75 % pour 25 % ») ; absent quand a = T/2 (il vaudrait la clé)' },
        rotation: {
            fraction_a_virgule: '4/5 lu « 4,5 » (Eduscol 5e 2025 q.12 « traduit la fraction par une virgule », q.20 « 1/2 = 1,5 ») ; toujours au-dessus de 1',
            numerateur_en_pourcent: '4/5 lu « 4 % » (fiche 5e 1082 « numérateur seul »)',
            inverse: '5/4 pour 4/5 (fiche 1082, DEPP 4e 2023 q.15) ; au-dessus de 1 ; absent quand a = 1 (T/1 n\'est pas une fraction)',
            decalage_de_virgule: '12/25 → « 4,8 » (48 décalé) ou 0,8 → « 0,08 » (fiche 1082 « 2,5 »)',
            pourcent_sans_cent: '0,48 → « 0,48 % »',
            un_sur_n: '0,2 → 1/2, 0,48 → 1/48 : les chiffres après la virgule pris pour un dénominateur (DEPP 4e 2023 q.16, distracteur 1/3 pour 0,3) ; absent quand ces chiffres valent moins de 2',
        },
        garantie: 'quatre options distinctes par texte ET par valeur, jamais égales à la clé, au plus une au-dessus de 1 (throw sinon) ; la clé recalculée depuis les effectifs relus dans l\'énoncé',
    },
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    ecritures_texte_trou_saisie: genChaineTrous,
    ecritures_qcm_unique: genEcrituresQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'ecritures', label: L_ECRITURES, formatsList: buildFormats('ecritures') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

/** Fonctions internes exposées à l'auto-test (énumérations exhaustives). */
export const _interne = { candidats, choisirDistracteurs, admissible, ecrituresDe, lignesChaine, motsAttendus, SACRIFIES, ECRITURES };
